/**
 * The tab's icon and title, and the colour of an installed window's title bar, follow the
 * section the route belongs to.
 *
 * Every path here is a file `scripts/vite-brand.mjs` generates from HabLogo — none is
 * in `public/` — and `test/brand.test.ts` holds this table to that list, so a renamed
 * file fails a test instead of leaving the tab blank.
 *
 * TWO MAPS, because the two links take different formats. A browser tab takes the
 * SVG; iOS ignores an SVG touch icon outright and falls back to a screenshot, so the
 * touch icon is the PNG. The previous code set the SVG on both.
 */

import { ref, shallowRef, watchEffect } from "vue";

export type BrandSection = "landing" | "designer" | "operator" | "control-center";

export const faviconBySection: Record<BrandSection, string> = {
	landing: "/brand/favicon-landing.svg",
	designer: "/brand/favicon-designer.svg",
	operator: "/brand/favicon-operator.svg",
	"control-center": "/brand/favicon-control-center.svg",
};

export const touchIconBySection: Record<BrandSection, string> = {
	landing: "/brand/apple-touch-icon-landing.png",
	designer: "/brand/apple-touch-icon-designer.png",
	operator: "/brand/apple-touch-icon-operator.png",
	"control-center": "/brand/apple-touch-icon-control-center.png",
};

/**
 * What a browser paints its own chrome with — an installed app's title bar, a phone's address
 * bar — each section's ground, the tile of its favicon: teal on the landing page and in the
 * Operator, purple in the Designer, blue in the control center.
 *
 * Literals, as `SECTION_COLORS` is, and held to it by `test/brand.test.ts`: a `<meta>` has no
 * stylesheet to resolve `var()` against either.
 */
export const themeColorBySection: Record<BrandSection, string> = {
	landing: "#009688",
	designer: "#5a4495",
	operator: "#009688",
	"control-center": "#37a3dd",
};

const titleBySection: Record<BrandSection, string> = {
	landing: "Notitia",
	designer: "Notitia Designer",
	operator: "Notitia Operator",
	"control-center": "Notitia Control Center",
};

/** A section's own title, for a page that belongs to a section its route does not (the dataspace list). */
export function sectionTitle(section: BrandSection): string {
	return titleBySection[section];
}

/** The head link with `rel`, created if the document has none yet. */
function headLink(rel: string): HTMLLinkElement {
	let link = document.querySelector<HTMLLinkElement>(`link[rel='${rel}']`);
	if (!link) {
		link = document.createElement("link");
		link.rel = rel;
		document.head.appendChild(link);
	}
	return link;
}

/** The head `<meta>` named `name`, created if the document has none yet. */
function headMeta(name: string): HTMLMetaElement {
	let meta = document.querySelector<HTMLMetaElement>(`meta[name='${name}']`);
	if (!meta) {
		meta = document.createElement("meta");
		meta.name = name;
		document.head.appendChild(meta);
	}
	return meta;
}

export function resolveSection(path: string, metaSection: unknown): BrandSection {
	if (
		metaSection === "designer" ||
		metaSection === "operator" ||
		metaSection === "landing" ||
		metaSection === "control-center"
	) {
		return metaSection;
	}
	if (path.startsWith("/designer")) return "designer";
	if (path.startsWith("/operator")) return "operator";
	if (path.startsWith("/control-center")) return "control-center";
	return "landing";
}

/*
  The title is the section's until a page says what is open in it — the Designer its project and
  page, the Operator its dataspace and process (`useWindowTitle`). Each mounted page registers a
  getter; the last registered that has something to say wins, so a view inside a layout outranks
  the layout, and a getter that returns `null` (nothing selected yet, or still loading) leaves the
  section's own title. One effect writes it, so the router setting the section and a page's name
  arriving later cannot overwrite each other in the wrong order.
*/
const currentSection = ref<BrandSection>("landing");
const titleSources = shallowRef<(() => string | null)[]>([]);

watchEffect(() => {
	const sources = titleSources.value;
	for (let i = sources.length - 1; i >= 0; i--) {
		const title = sources[i]();
		if (title) {
			document.title = title;
			return;
		}
	}
	document.title = titleBySection[currentSection.value];
});

/** Registers a page's title; the returned function takes it back. */
export function registerWindowTitle(source: () => string | null): () => void {
	titleSources.value = [...titleSources.value, source];
	return () => {
		titleSources.value = titleSources.value.filter((s) => s !== source);
	};
}

export function applyBranding(section: BrandSection) {
	headLink("icon").href = faviconBySection[section];
	headLink("apple-touch-icon").href = touchIconBySection[section];
	headMeta("theme-color").content = themeColorBySection[section];
	currentSection.value = section;
}
